import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../utils/apiFetch";
import { Dialog } from "@material-ui/core";
import ProfileDetailsFields, { profileToDraft, draftToProfile } from "./ProfileDetailsFields";

import "./global/global.css";

const inputStyle = {
  padding: "6px 10px",
  fontSize: "13px",
  border: "1px solid #d0d0d0",
  borderRadius: "4px",
  color: "#333",
  backgroundColor: "#fff",
};

const typeBadges = (u) =>
  [
    u.is_staff && ["Admin", "status-seen"],
    u.is_guest && ["Guest", "status-pending"],
    !u.is_active && ["Inactive", "status-failed"],
  ].filter(Boolean);

const buttonStyle = (color) => ({
  padding: "4px 10px",
  fontSize: "12px",
  borderRadius: "4px",
  border: `1px solid ${color}`,
  background: "#fff",
  color,
  cursor: "pointer",
});

const errorStyle = {
  padding: "10px 14px",
  backgroundColor: "#fce4ec",
  color: "#b71c1c",
  borderRadius: "6px",
  fontSize: "14px",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const dialogStyle = {
  background: "#fff",
  color: "#333",
  fontFamily: "lexend, sans-serif",
  borderRadius: "8px",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
  padding: "24px",
  width: "480px",
  maxHeight: "85vh",
  overflowY: "auto",
};

const FLAG_FIELDS = [
  ["is_staff", "Admin (staff)"],
  ["is_active", "Active"],
  ["is_guest", "Guest"],
];

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const countLines = (counts) =>
  Object.entries(counts || {}).map(([label, n]) => (
    <li key={label}>
      {n} {label}
    </li>
  ));

const EditDialog = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const mounted = useRef(true);
  const apiUrl = `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/users/${encodeURIComponent(user.email)}/`;

  useEffect(() => {
    let active = true;
    mounted.current = true;
    const load = async () => {
      try {
        const response = await apiFetch(apiUrl);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to load user");
        if (active) {
          setForm({
            ...profileToDraft(data.user),
            is_staff: data.user.is_staff,
            is_active: data.user.is_active,
            is_guest: data.user.is_guest,
            free_reports: data.user.free_reports,
          });
        }
      } catch (err) {
        if (active) setError(err.message);
      }
    };
    load();
    return () => {
      active = false;
      mounted.current = false;
    };
  }, [apiUrl]);

  const change = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError(null);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form || isSaving) return;
    setError(null);
    setIsSaving(true);
    try {
      const res = await apiFetch(apiUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...draftToProfile(form),
          is_staff: form.is_staff,
          is_active: form.is_active,
          is_guest: form.is_guest,
          free_reports: Number(form.free_reports),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save user");
      if (mounted.current) onSaved(data.user);
    } catch (err) {
      if (mounted.current) setError(err.message);
    } finally {
      if (mounted.current) setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={isSaving ? undefined : onClose}
      aria-labelledby="admin-profile-title"
      PaperProps={{ style: { ...dialogStyle, margin: "16px", maxWidth: "calc(100% - 32px)", boxSizing: "border-box" } }}
    >
      <form onSubmit={save}>
        <h3 id="admin-profile-title">Edit user</h3>
        <p style={{ color: "#777", fontSize: "13px" }}>{user.email} (cannot be changed)</p>
        {error && <p role="alert" style={errorStyle}>{error}</p>}
        {!form && !error && <p role="status">Loading user...</p>}
        {form && (
          <>
            <ProfileDetailsFields
              draft={form}
              email={user.email}
              onChange={change}
              disabled={isSaving}
              idPrefix="admin-profile"
            />
            <label style={{ display: "block", marginBottom: "10px", fontSize: "13px" }}>
              Free reports
              <input
                aria-label="Free reports"
                type="number"
                disabled={isSaving}
                style={{ ...inputStyle, display: "block", width: "100%", boxSizing: "border-box" }}
                value={form.free_reports}
                onChange={(event) => change("free_reports", event.target.value)}
              />
            </label>
            {FLAG_FIELDS.map(([key, label]) => (
              <label key={key} style={{ display: "block", marginBottom: "6px", fontSize: "13px" }}>
                <input
                  type="checkbox"
                  aria-label={label}
                  disabled={isSaving}
                  checked={form[key]}
                  onChange={(event) => change(key, event.target.checked)}
                />{" "}
                {label}
              </label>
            ))}
          </>
        )}
        <div style={{ marginTop: "16px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} disabled={isSaving} style={buttonStyle("#555")}>
            Cancel
          </button>
          <button type="submit" disabled={!form || isSaving} style={buttonStyle("#28a382")}>
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const DeleteDialog = ({ user, onClose, onDeleted }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const apiUrl = `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/users/${encodeURIComponent(user.email)}/`;

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(apiUrl);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load preview");
        setDetail(data.user);
      } catch (err) {
        setError(err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmDelete = async () => {
    setError(null);
    try {
      const res = await apiFetch(apiUrl, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete user");
      onDeleted(user.email);
    } catch (err) {
      setError(err.message);
    }
  };

  const preview = detail && detail.deletion_preview;
  const blockedReason = preview && preview.blocked_reason;
  const canDelete = detail && detail.can_delete && !blockedReason;

  return (
    <div style={overlayStyle}>
      <div style={dialogStyle}>
        <h3>Delete {user.email}?</h3>
        {error && <p style={errorStyle}>{error}</p>}
        {!detail && !error && <p className="loading">Loading preview...</p>}
        {detail && (
          <>
            {Object.keys(preview.removed).length > 0 ? (
              <>
                <p style={{ fontSize: "14px" }}>This permanently removes:</p>
                <ul style={{ fontSize: "14px" }}>{countLines(preview.removed)}</ul>
              </>
            ) : (
              <p style={{ fontSize: "14px" }}>This user owns no other records.</p>
            )}
            {Object.keys(preview.detached).length > 0 && (
              <>
                <p style={{ fontSize: "14px" }}>These records are kept but unlinked:</p>
                <ul style={{ fontSize: "14px" }}>{countLines(preview.detached)}</ul>
              </>
            )}
            <p style={{ fontSize: "12px", color: "#777" }}>
              Database only: the Cognito, Stripe and Plaid accounts are not touched.
            </p>
            {blockedReason && <p style={errorStyle}>{blockedReason}</p>}
            {!canDelete && !blockedReason && (
              <p style={errorStyle}>
                This user cannot be deleted (yourself, or an admin — remove admin access first).
              </p>
            )}
          </>
        )}
        <div style={{ marginTop: "16px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={buttonStyle("#555")}>
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            disabled={!canDelete}
            style={{ ...buttonStyle("#c62828"), opacity: canDelete ? 1 : 0.5 }}
          >
            Delete user
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/users/`);
        if (!res.ok) throw new Error("Failed to fetch users");
        const data = await res.json();
        setUsers(data.users || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const needle = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    const haystack = `${u.email} ${u.first_name} ${u.last_name}`.toLowerCase();
    if (needle && !haystack.includes(needle)) return false;
    if (typeFilter === "admin" && !u.is_staff) return false;
    if (typeFilter === "guest" && !u.is_guest) return false;
    if (typeFilter === "regular" && (u.is_staff || u.is_guest)) return false;
    return true;
  });

  const header = (subtitle) => (
    <div
      className="client-list-heading"
      style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}
    >
      <div className="lc">
        <h3 className="cl-rh">Users</h3>
        <p className="cl-rh-sub">{subtitle}</p>
      </div>
    </div>
  );

  if (loading || error) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          {header("All users, including guests.")}
          <div className="cl-table-container">
            {loading ? <p className="loading">Loading...</p> : <p className="error">Error: {error}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        {header(
          needle || typeFilter
            ? `Showing ${filtered.length} of ${users.length} users`
            : `${users.length} user${users.length !== 1 ? "s" : ""}`
        )}
        <div style={{ display: "flex", gap: "12px", padding: "16px 20px 0", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputStyle, minWidth: "240px" }}
          />
          <select
            aria-label="User type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Users</option>
            <option value="regular">Regular</option>
            <option value="admin">Admins</option>
            <option value="guest">Guests</option>
          </select>
        </div>
        <div className="cl-table-container">
          {filtered.length === 0 ? (
            <p style={{ color: "#777", fontSize: "14px" }}>
              {users.length === 0 ? "No users found." : "No users match the current filters."}
            </p>
          ) : (
            <table className="cl-styled-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th style={{ textAlign: "center" }}>Deals</th>
                  <th>Joined</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.email}>
                    <td style={{ fontWeight: 500, color: "#333" }}>{u.email}</td>
                    <td>{`${u.first_name} ${u.last_name}`.trim() || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {typeBadges(u).map(([label, cls]) => (
                        <span key={label} className={cls} style={{ marginRight: "6px" }}>
                          {label}
                        </span>
                      ))}
                      {typeBadges(u).length === 0 && <span className="status-sent">User</span>}
                    </td>
                    <td style={{ textAlign: "center" }}>{u.deal_count}</td>
                    <td>{formatDate(u.date_joined)}</td>
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <button
                        onClick={() => setEditing(u)}
                        style={{ ...buttonStyle("#555"), marginRight: "8px" }}
                      >
                        Edit
                      </button>
                      <button onClick={() => setDeleting(u)} style={buttonStyle("#c62828")}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {editing && (
        <EditDialog
          key={editing.email}
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setUsers((prev) =>
              prev.map((u) => (u.email === saved.email ? { ...u, ...saved, deal_count: u.deal_count } : u))
            );
            setEditing(null);
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(email) => {
            setUsers((prev) => prev.filter((u) => u.email !== email));
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminUsers;
